import React, { useEffect, useState } from "react";
import { XMarkIcon, EnvelopeIcon } from "@heroicons/react/24/outline";
import { Button } from "../../ui";
import { getVaptNotificationPreviewApi } from "../../../services/axios/AuthApis/VAPTApis";

// Read-only customer notification email preview. Never sends anything —
// the "Send" action is a no-op that just closes the modal, matching the
// module-wide rule that no real email/notification integration exists.
const VaptNotificationPreviewModal = ({ projectId, onClose, onPreviewOnly }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    getVaptNotificationPreviewApi(projectId).then((res) => {
      if (mounted) {
        setData(res);
        setLoading(false);
      }
    });
    return () => {
      mounted = false;
    };
  }, [projectId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <EnvelopeIcon className="w-5 h-5 text-gray-400" />
            <h3 className="text-sm font-bold text-gray-900">Customer Notification Preview</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <p className="text-[11px] font-semibold text-gray-500 mb-4">Preview of the customer notification. Delivery is managed from Coverage &amp; Exceptions.</p>

        {loading && <p className="text-sm text-gray-400">Loading preview…</p>}

        {!loading && !data && (
          <p className="text-sm text-gray-400">Notification preview is unavailable until a backend preview is configured.</p>
        )}

        {!loading && data && (
          <div className="space-y-4 text-sm">
            <div>
              <p className="text-[10px] text-gray-400 uppercase">Subject</p>
              <p className="text-gray-800 font-medium">{data.subject}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 uppercase">Recipients</p>
              <p className="text-gray-800">{(data.recipients || []).join(", ") || "—"}</p>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {Object.entries(data.summary || {}).map(([k, v]) => (
                <div key={k} className="border border-gray-200 rounded-lg p-2 text-center">
                  <p className="text-[10px] text-gray-400 capitalize">{k.replace(/([A-Z])/g, " $1")}</p>
                  <p className="text-lg font-bold text-gray-800">{v}</p>
                </div>
              ))}
            </div>
            {data.findingsBySeverity && (
              <div>
                <p className="text-[10px] text-gray-400 uppercase mb-1">Findings by Severity</p>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(data.findingsBySeverity).map(([sev, count]) => (
                    <span key={sev} className="text-xs text-gray-700">
                      {sev}: <span className="font-semibold">{count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {data.unsupported && data.unsupported.length > 0 && (
              <div>
                <p className="text-[10px] text-gray-400 uppercase mb-1">Unsupported / Not Applicable</p>
                <ul className="space-y-1">
                  {data.unsupported.map((u) => (
                    <li key={u.target} className="text-xs text-gray-700">
                      <span className="font-medium">{u.target}</span> — {u.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-6">
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button
            onClick={() => {
              onPreviewOnly && onPreviewOnly();
              onClose();
            }}
          >
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VaptNotificationPreviewModal;
